import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { useGoBack } from "../../../shared/phone/history";
import { useNative, useRail } from "../../../shared/phone/media";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { Split } from "../../../shared/phone/Split";
import { NeedPane, NeedSheet, NothingWaits, type NeedFlowProps } from "../cc/home/NeedView";
import { useOpenNeed } from "../cc/home/HomeNative";
import { closeSheet, ProductMark, useHmToast, WaitRow } from "../cc/home/parts";
import { byGoal, inGoalOrder, waitingRows } from "../cc/home/waiting";
import { shortGoal } from "../cc/text";
import { useHome } from "../cc/useCc";
import { useCan } from "../../context/AuthContext";

const enc = encodeURIComponent;

/**
 * Needs you (`/needs`, `/needs/:id`): every decision waiting for the owner, grouped by goal. A phone opens the
 * one tapped in a sheet; an iPad shows it beside the list. A computer keeps them on each goal's page.
 */
export function NeedsView() {
  return useNative() ? <NeedsNative /> : <NeedsWide />;
}

/** On a computer, a link to a need opens it on its goal's page. */
function NeedsWide() {
  const { id } = useParams();
  const { home } = useHome();
  const can = useCan();
  if (!id) return <Navigate to="/command" replace />;
  if (!home) return null;
  return <Navigate to={waitingRows(home, can).find((w) => w.key === id)?.to ?? "/command"} replace />;
}

function NeedsNative() {
  const rail = useRail();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { id = null } = useParams();
  const { home, error } = useHome();
  const [gone, setGone] = useState<ReadonlySet<string>>(new Set());
  const { toast, say } = useHmToast();
  const can = useCan();
  const rows = useMemo(() => (home ? inGoalOrder(waitingRows(home, can)).filter((r) => !gone.has(r.key)) : []), [home, can, gone]);
  const cur = useOpenNeed(rows, id);

  // On an iPad the first one shows beside the list until another is picked.
  const toFirst = rail && home && !id && rows[0] ? `/needs/${enc(rows[0].key)}` : null;
  useEffect(() => {
    if (toFirst) navigate(toFirst, { replace: true });
  }, [toFirst, navigate]);
  const stale = !!home && !!id && !cur;
  useEffect(() => {
    if (stale) navigate("/needs", { replace: true });
  }, [stale, navigate]);

  const flow: Omit<NeedFlowProps, "current"> = {
    rows,
    open: (key) => navigate(`/needs/${enc(key)}`, { replace: true }),
    done: () => (rail ? navigate("/needs", { replace: true }) : closeSheet()),
    go: (to) => navigate(to, { replace: !rail }),
    gone: (key) => setGone((s) => new Set(s).add(key)),
    say,
  };

  const list = (
    <div className="pn-screen hm-needs">
      <PhoneNav large title="Needs you" sub={rows.length ? `${rows.length} ${rows.length === 1 ? "thing waits" : "things wait"} for you` : undefined} />
      {!home ? (
        <p className="pn-note">{error ?? "Loading…"}</p>
      ) : rows.length ? (
        <div className="pn-stack tight">
          {byGoal(rows).map((g) => (
            <section key={g.goal.id}>
              <div className="pn-cap hm-capg">
                <ProductMark p={g.product} size={18} />
                <span>{shortGoal(g.goal.sentence)}</span>
              </div>
              <div className="pn-grp">
                {g.rows.map((w) => (
                  <WaitRow key={w.key} w={w} to={`/needs/${enc(w.key)}`} sub={w.line} on={rail && id === w.key} />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <NothingWaits />
      )}
    </div>
  );

  return (
    <>
      <Split list={list} detail={cur ? <NeedPane {...flow} current={cur} /> : <div className="pn-screen" />} showDetail={false} />
      {!rail && cur && <NeedSheet {...flow} current={cur} onClose={() => goBack("/needs")} />}
      {toast}
    </>
  );
}
